import { Monitor, Moon, Sun } from "lucide-react";
import { DevicePickers } from "@/components/settings/DevicePickers";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogClose,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { ToggleGroup, ToggleGroupItem } from "@/components/ui/toggle-group";
import { useMirrorPreference } from "@/hooks/usePreferences";
import { useTheme } from "@/hooks/useTheme";

const THEME_OPTIONS = [
  { value: "system", label: "System", icon: Monitor },
  { value: "light", label: "Light", icon: Sun },
  { value: "dark", label: "Dark", icon: Moon },
];

const SECTION_TRIGGER =
  "h-auto flex-none justify-start px-3 py-1.5 data-[state=active]:bg-muted data-[state=active]:shadow-none dark:data-[state=active]:border-transparent dark:data-[state=active]:bg-muted";

const ThemeChoice = () => {
  const { theme, setTheme } = useTheme();
  return (
    <ToggleGroup
      type="single"
      value={theme}
      onValueChange={value => value && setTheme(value)}
      aria-label="Theme"
      className="grid w-full grid-cols-3 gap-2"
    >
      {THEME_OPTIONS.map(({ value, label, icon: Icon }) => (
        <ToggleGroupItem
          key={value}
          value={value}
          data-testid={`settings_theme_${value}`}
          className="h-auto flex-col gap-2 rounded-md border py-4 first:rounded-md last:rounded-md data-[state=on]:border-foreground data-[state=on]:bg-card"
        >
          <Icon className="size-5" />
          {label}
        </ToggleGroupItem>
      ))}
    </ToggleGroup>
  );
};

/**
 * Camera, microphone and speaker, mirroring of your own video, and the
 * colour theme, after Cal.com's settings dialog.
 */
export const SettingsDialog = ({ open, onOpenChange }) => {
  const [mirror, setMirror] = useMirrorPreference();

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="gap-0 p-0 sm:max-w-2xl">
        <DialogHeader className="border-b px-5 py-4">
          <DialogTitle className="text-base">Settings</DialogTitle>
          <DialogDescription>Device Settings and appearance</DialogDescription>
        </DialogHeader>
        <Tabs
          defaultValue="devices"
          orientation="vertical"
          className="gap-0 sm:min-h-80 sm:flex-row"
        >
          <TabsList className="h-auto w-full justify-start gap-1 rounded-none border-b bg-transparent p-2 sm:w-44 sm:flex-col sm:items-stretch sm:justify-start sm:border-r sm:border-b-0">
            <TabsTrigger value="devices" className={SECTION_TRIGGER}>
              Audio &amp; video
            </TabsTrigger>
            <TabsTrigger
              value="appearance"
              className={SECTION_TRIGGER}
              data-testid="settings_appearance_tab"
            >
              Appearance
            </TabsTrigger>
          </TabsList>
          <TabsContent
            value="devices"
            className="grid min-w-0 grid-cols-1 content-start gap-5 p-5"
          >
            <DevicePickers />
            <div className="flex items-center justify-between gap-4 rounded-md border p-3">
              <div className="grid gap-0.5">
                <Label htmlFor="mirror">Mirror my video</Label>
                <p className="text-xs text-muted-foreground">
                  Only you see your video mirrored.
                </p>
              </div>
              <Switch
                id="mirror"
                checked={mirror}
                onCheckedChange={setMirror}
                data-testid="mirror_switch"
              />
            </div>
          </TabsContent>
          <TabsContent
            value="appearance"
            className="grid min-w-0 grid-cols-1 content-start gap-3 p-5"
          >
            <div className="grid gap-0.5">
              <p className="text-sm font-medium">Theme</p>
              <p className="text-xs text-muted-foreground">
                System follows your device. The video area stays dark in every
                theme.
              </p>
            </div>
            <ThemeChoice />
          </TabsContent>
        </Tabs>
        <DialogFooter className="m-0 px-5 py-3">
          <DialogClose asChild>
            <Button>Done</Button>
          </DialogClose>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
